import {
  PostHogDashboardResponseDto,
  PostHogInsightCatalogEntryDto,
  PostHogInsightResponseDto,
} from "@swagger/typescript-fetch-goflyer";
import { AuthHeadersRequestOptions } from "../../hooks/useAuthHeaders";
import { adminApiServiceRaw } from "../api";
import {
  AnalyticsCatalogItem,
  AnalyticsDashboardResults,
  AnalyticsFilters,
  DEFAULT_ANALYTICS_FILTERS,
  InsightKey,
} from "./types";

export class AnalyticsApiError extends Error {
  status: number;

  constructor(status: number, message?: string) {
    super(message ?? `Analytics error: ${status}`);
    this.status = status;
    this.name = "AnalyticsApiError";
  }
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return error instanceof Error && error.name === "AbortError";
}

async function toAnalyticsApiError(error: unknown): Promise<AnalyticsApiError> {
  if (isAbortError(error)) {
    throw error;
  }

  if (error instanceof AnalyticsApiError) {
    return error;
  }

  const response = error as Response;
  if (typeof response?.status === "number" && typeof response?.json === "function") {
    try {
      const body = (await response.json()) as {
        message?: string;
        error?: string;
      };
      return new AnalyticsApiError(
        response.status,
        body.message ?? body.error,
      );
    } catch {
      return new AnalyticsApiError(response.status);
    }
  }

  if (error instanceof Error && error.message) {
    return new AnalyticsApiError(500, error.message);
  }

  return new AnalyticsApiError(500);
}

function mergeFilters(
  filters: AnalyticsFilters = DEFAULT_ANALYTICS_FILTERS,
): AnalyticsFilters {
  return { ...DEFAULT_ANALYTICS_FILTERS, ...filters };
}

async function invokeAnalyticsApi<T>(
  fn: () => Promise<T | undefined>,
): Promise<T> {
  try {
    const result = await fn();
    if (result === undefined) {
      throw new AnalyticsApiError(
        500,
        "Analytics SDK returned an empty response",
      );
    }
    return result;
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toAnalyticsApiError(error);
  }
}

export async function fetchAnalyticsCatalog(
  authHeaders: AuthHeadersRequestOptions,
): Promise<AnalyticsCatalogItem[]> {
  const catalog = await invokeAnalyticsApi(() =>
    adminApiServiceRaw.postHogAnalyticsAdminControllerGetInsightCatalog(
      authHeaders,
    ),
  );

  return catalog.map((entry: PostHogInsightCatalogEntryDto) => ({
    key: String(entry.key) as InsightKey,
    label: entry.label,
    description: entry.description,
    defaultDateRange: entry.defaultDateRange,
  }));
}

export async function fetchAnalyticsInsight(
  key: InsightKey,
  filters: AnalyticsFilters,
  authHeaders: AuthHeadersRequestOptions,
): Promise<PostHogInsightResponseDto> {
  const merged = mergeFilters(filters);
  return invokeAnalyticsApi(() =>
    adminApiServiceRaw.postHogAnalyticsAdminControllerGetInsight(
      key,
      merged.merchantId,
      merged.storeId,
      merged.categoryId,
      merged.dateFrom,
      merged.dateTo,
      authHeaders,
    ),
  );
}

export async function fetchAnalyticsDashboard(
  filters: AnalyticsFilters,
  authHeaders: AuthHeadersRequestOptions,
): Promise<AnalyticsDashboardResults> {
  const merged = mergeFilters(filters);
  const body = await invokeAnalyticsApi<PostHogDashboardResponseDto>(() =>
    adminApiServiceRaw.postHogAnalyticsAdminControllerGetDashboard(
      merged.merchantId,
      merged.storeId,
      merged.categoryId,
      merged.dateFrom,
      merged.dateTo,
      authHeaders,
    ),
  );

  return (body?.results ?? {}) as AnalyticsDashboardResults;
}
